import {
  getHookVariantDisplayMeta,
  sortPersonaScriptsWithVariantsAfterParent,
} from "../../../lib/hook-variations-order.js";

import type { HubChromeScriptGroup, HubChromeScriptRow } from "./hub-chrome.types.js";
import type { HubOpportunitySort } from "./hub-opportunity-table.js";

/** Client-side groups for callers that hold every row already, in first-seen order. */
export function groupScriptRowsByOpportunity(
  rows: readonly HubChromeScriptRow[],
): HubChromeScriptGroup[] {
  const groups = new Map<string, HubChromeScriptGroup>();
  for (const row of rows) {
    const group = groups.get(row.opportunityId) ?? {
      key: row.opportunityId,
      label: row.opportunityAngle,
      personaTitle: null,
      isCustom: false,
      setId: row.setId,
      setName: row.setName,
      scriptCount: 0,
      briefCount: 0,
      rows: [],
      isLoading: false,
      sentItems: [],
      isLoadingSentTitles: false,
    };
    group.rows.push(row);
    if (row.sent) group.sentItems.push({ id: row.id, title: row.title, vendorName: null });
    if (row.kind === "script") group.scriptCount += 1;
    else group.briefCount += 1;
    groups.set(row.opportunityId, group);
  }
  return [...groups.values()].map((group) => ({
    ...group,
    rows: sortScriptGroupRows(group.rows),
  }));
}

function scriptSortValue(
  row: HubChromeScriptRow,
  columnId: string,
): string | number | null {
  switch (columnId) {
    case "kind":
      return row.kind;
    case "title":
      return row.title;
    case "generatedAt": {
      const time = Date.parse(row.createdAt);
      return Number.isNaN(time) ? null : time;
    }
    case "sent":
      return row.sent ? 1 : 0;
    default:
      return null;
  }
}

/**
 * Scripts by slot (S1, S2, …) with each parent's hook variants right after it,
 * labelled `S1·H1`, then briefs by slot — the single-set Scripts tab order.
 */
export function sortScriptGroupRows(rows: readonly HubChromeScriptRow[]): HubChromeScriptRow[] {
  const scripts = rows.filter((row) => row.kind === "script");
  const briefs = rows
    .filter((row) => row.kind === "brief")
    .sort((left, right) => left.slotIndex - right.slotIndex || left.id.localeCompare(right.id));
  const orderedScripts = sortPersonaScriptsWithVariantsAfterParent(scripts).map((row) => ({
    ...row,
    variantMeta: getHookVariantDisplayMeta(row, scripts),
  }));
  return [...orderedScripts, ...briefs];
}

/** Blank values sink in both directions, matching the opportunities table. */
export function sortScriptRows(
  rows: HubChromeScriptRow[],
  sort: HubOpportunitySort | null,
): HubChromeScriptRow[] {
  if (!sort) return rows;
  const factor = sort.direction === "asc" ? 1 : -1;

  return [...rows].sort((left, right) => {
    const a = scriptSortValue(left, sort.columnId);
    const b = scriptSortValue(right, sort.columnId);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === "number" && typeof b === "number") return (a - b) * factor;
    return String(a).localeCompare(String(b)) * factor;
  });
}

export type ScriptListItem =
  | { kind: "group"; key: string; group: HubChromeScriptGroup; expanded: boolean }
  | { kind: "rows"; key: string; groupKey: string; rows: HubChromeScriptRow[] }
  | { kind: "loading"; key: string; groupKey: string }
  | { kind: "empty"; key: string; groupKey: string };

/**
 * Flat virtual-list items: each header, then — when expanded — its rows in
 * chunks of `rowsPerItem` (two cards share a line), a loading line, or an
 * empty line when the filters hide every loaded row. A column sort reorders
 * rows inside their group only.
 */
export function scriptListItems(
  groups: readonly HubChromeScriptGroup[],
  expandedKeys: ReadonlySet<string>,
  sort: HubOpportunitySort | null,
  rowsPerItem: number,
): ScriptListItem[] {
  const items: ScriptListItem[] = [];
  for (const group of groups) {
    const expanded = expandedKeys.has(group.key);
    items.push({ kind: "group", key: `group:${group.key}`, group, expanded });
    if (!expanded) continue;
    if (group.isLoading) {
      items.push({ kind: "loading", key: `loading:${group.key}`, groupKey: group.key });
      continue;
    }
    if (group.rows.length === 0) {
      items.push({ kind: "empty", key: `empty:${group.key}`, groupKey: group.key });
      continue;
    }
    const rows = sortScriptRows(group.rows, sort);
    for (let start = 0; start < rows.length; start += rowsPerItem) {
      const chunk = rows.slice(start, start + rowsPerItem);
      items.push({
        kind: "rows",
        key: chunk.map((row) => row.id).join(":"),
        groupKey: group.key,
        rows: chunk,
      });
    }
  }
  return items;
}
